import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PendingVerifications.css";
import "./NewLeaveByOffice.css";

const API_ROOT      = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const ALLOWED_MODULES = [2, 4, 9, 15];

const getToday = () => new Date().toISOString().slice(0, 10);

const calcNoOfDays = (fromDate, toDate, hfDay) => {
  if (!fromDate || !toDate) return "";
  const start = new Date(fromDate);
  const end   = new Date(toDate);
  if (isNaN(start) || isNaN(end) || start > end) return "";
  const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  if (hfDay === "F" || hfDay === "S") {
    return diffDays === 1 ? "0.5" : String(diffDays - 0.5);
  }
  return String(diffDays);
};

export default function NewLeaveByOffice() {
  const navigate = useNavigate();

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const deptId   = parseInt(localUser?.deptid,   10);
  const empId    = parseInt(localUser?.id,        10);

  const [employees,    setEmployees]    = useState([]);
  const [leaveTypes,   setLeaveTypes]   = useState([]);
  const [leaveAvailed, setLeaveAvailed] = useState("");
  const [loadingInit,  setLoadingInit]  = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState("");
  const [success,      setSuccess]      = useState("");

  const [form, setForm] = useState({
    EmployeeID:  "",
    LeaveTypeID: "",
    FromDate:    getToday(),
    ToDate:      "",
    NoOfDays:    "",
    HFDay:       "W",
    Remarks:     "",
    Status:      "Pending",
    AppDate:     getToday(),
  });

  // Load employee list + leave types on mount
  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) return;
    const load = async () => {
      setLoadingInit(true);
      try {
        const empParams = new URLSearchParams({ moduleId });
        if ([2, 15].includes(moduleId)) empParams.append("deptId", deptId);
        if (moduleId === 9)             empParams.append("empId",  empId);

        const [empRes, ltRes] = await Promise.all([
          fetch(`${API_ROOT}/api/employees/leave-employees-office?${empParams}`),
          fetch(`${API_ROOT}/api/employees/leave-types-office?moduleId=${moduleId}`),
        ]);
        const empData = await empRes.json();
        const ltData  = await ltRes.json();
        setEmployees(Array.isArray(empData.data) ? empData.data : []);
        setLeaveTypes(Array.isArray(ltData)       ? ltData       : []);
      } catch {
        setError("Failed to load form data.");
      } finally {
        setLoadingInit(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  // Auto-fetch Leave Availed when employee + leave type are both selected
  useEffect(() => {
    if (!form.EmployeeID || !form.LeaveTypeID) {
      setLeaveAvailed("");
      return;
    }
    const fetch_ = async () => {
      try {
        const res  = await fetch(
          `${API_ROOT}/api/employees/leave-availed?moduleId=${moduleId}&employeeid=${form.EmployeeID}&leavetypeid=${form.LeaveTypeID}`
        );
        const data = await res.json();
        setLeaveAvailed(data.status === "success" ? String(data.data ?? "0.00") : "0.00");
      } catch {
        setLeaveAvailed("0.00");
      }
    };
    fetch_();
  }, [form.EmployeeID, form.LeaveTypeID, moduleId]);

  // Auto-calculate NoOfDays whenever dates or HFDay change
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      NoOfDays: calcNoOfDays(prev.FromDate, prev.ToDate, prev.HFDay),
    }));
  }, [form.FromDate, form.ToDate, form.HFDay]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.EmployeeID || !form.LeaveTypeID || !form.FromDate || !form.ToDate || !form.NoOfDays) {
      setError("All required fields must be filled.");
      return;
    }
    if (new Date(form.FromDate) > new Date(form.ToDate)) {
      setError("From Date cannot be after To Date.");
      return;
    }

    try {
      setSubmitting(true);
      const res  = await fetch(`${API_ROOT}/api/employees/leave-by-office`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          moduleId,
          empId,
          EmployeeID:  form.EmployeeID,
          LeaveTypeID: form.LeaveTypeID,
          FromDate:    form.FromDate,
          ToDate:      form.ToDate,
          NoOfDays:    form.NoOfDays,
          HFDay:       form.HFDay,
          Remarks:     form.Remarks,
          Status:      form.Status,
          AppDate:     form.AppDate,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to submit");

      setSuccess(data.message || "Leave record added successfully.");
      setLeaveAvailed("");
      setForm({
        EmployeeID:  "",
        LeaveTypeID: "",
        FromDate:    getToday(),
        ToDate:      "",
        NoOfDays:    "",
        HFDay:       "W",
        Remarks:     "",
        Status:      "Pending",
        AppDate:     getToday(),
      });
    } catch (err) {
      setError(err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ALLOWED_MODULES.includes(moduleId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex align-items-center mb-3 gap-2">
        <h3 className="ctitle mb-0">New Leave By Office</h3>
        <button
          className="btn btn-secondary btn-sm ms-3"
          onClick={() => navigate("/leave-details-office")}
        >
          Leave Details
        </button>
      </div>

      {error   && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={onSubmit}>
        <div className="row g-2">

          <div className="col-sm-4">
            <label className="form-label mb-1">Employee <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="EmployeeID"
              value={form.EmployeeID}
              onChange={onChange}
              disabled={loadingInit}
              required
            >
              <option value="">-- Select Employee --</option>
              {employees.map(e => (
                <option key={e.EmployeeID} value={e.EmployeeID}>
                  {[e.EmpName, e.Designation, e.Department, e.Mobile].filter(Boolean).join(" - ")}
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-3">
            <label className="form-label mb-1">Leave Type <span className="text-danger">*</span></label>
            <select
              className="form-select"
              name="LeaveTypeID"
              value={form.LeaveTypeID}
              onChange={onChange}
              disabled={loadingInit}
              required
            >
              <option value="">-- Select Leave Type --</option>
              {leaveTypes.map(lt => (
                <option key={lt.LeaveTypeID} value={lt.LeaveTypeID}>{lt.LeaveType}</option>
              ))}
            </select>
          </div>

          <div className="col-sm-2">
            <label className="form-label mb-1">Leave Availed</label>
            <input
              className="form-control"
              type="text"
              value={leaveAvailed}
              readOnly
              placeholder="0.00"
            />
          </div>

          <div className="col-sm-3">
            <label className="form-label mb-1">Half / Full Day</label>
            <select
              className="form-select"
              name="HFDay"
              value={form.HFDay}
              onChange={onChange}
            >
              <option value="W">Full Day</option>
              <option value="F">First Half</option>
              <option value="S">Second Half</option>
            </select>
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">From Date <span className="text-danger">*</span></label>
            <input
              className="form-control"
              type="date"
              name="FromDate"
              value={form.FromDate}
              onChange={onChange}
              required
            />
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">To Date <span className="text-danger">*</span></label>
            <input
              className="form-control"
              type="date"
              name="ToDate"
              value={form.ToDate}
              onChange={onChange}
              required
            />
          </div>

          <div className="col-auto nlbo-days">
            <label className="form-label mb-1">No of Days</label>
            <input
              className="form-control"
              type="text"
              name="NoOfDays"
              value={form.NoOfDays}
              onChange={onChange}
              placeholder="Auto"
            />
          </div>

          <div className="col-auto nlbo-status">
            <label className="form-label mb-1">Status</label>
            <select
              className="form-select"
              name="Status"
              value={form.Status}
              onChange={onChange}
            >
              <option value="Approved">Approved</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          <div className="col-auto nlbo-date">
            <label className="form-label mb-1">App Date</label>
            <input
              className="form-control"
              type="date"
              name="AppDate"
              value={form.AppDate}
              onChange={onChange}
            />
          </div>

          <div className="col">
            <label className="form-label mb-1">Remarks</label>
            <input
              className="form-control"
              type="text"
              name="Remarks"
              value={form.Remarks}
              onChange={onChange}
              maxLength={255}
              placeholder="Remarks..."
            />
          </div>

          <div className="col-12 mt-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || loadingInit}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}
